import { Send } from "lucide-react";
import * as React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useArtifacts } from "@/api/artifacts";
import { ApiError } from "@/api/client";
import { useUploadMolecule } from "@/api/molecules";
import { useFlowDag, useFlowDagOverlay, type DagNodeWithState } from "@/api/dag";
import { useFlowManifest, useFlows, useSubmitFlow } from "@/api/flows";
import { useProject } from "@/api/projects";
import { BulkRetryControl } from "@/components/dag/BulkRetryControl";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { ArtifactInputPicker, type InputPort } from "@/components/submission/ArtifactInputPicker";
import { SelectedInputPreview } from "@/components/submission/SelectedInputPreview";
import { FlowManifestPreview } from "@/components/submission/FlowManifestPreview";
import { FlowPicker } from "@/components/submission/FlowPicker";
import {
  ManifestForm,
  declaredParameterDefaults,
  renderableParameterEdits,
} from "@/components/submission/ManifestForm";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy-loaded so reactflow (~150 KB gz) only lands when a user
// opens the DAG tab, not on every Flow submission page load.
const DAGCanvas = React.lazy(() =>
  import("@/components/dag/DAGCanvas").then((m) => ({ default: m.DAGCanvas }))
);

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * Flow submission: picker → preview → inputs → submit (EPIC-001 U6).
 *
 * Two-column layout on desktop: Flow picker (left) → manifest preview +
 * DAG + input wiring (right).
 *
 * Submitting POSTs to /api/flows/submit, which compiles the template and
 * dispatches the DAG in the background — one run per fan-out child. There
 * is no flow-run id to navigate to (FlowStack has no flow-run table), so
 * the page stays put: the DAG below it is the live view, and its overlay
 * colors each node as its children reach terminal states.
 *
 * Flow inputs are catalog artifacts, referenced by content_hash — the same
 * currency the Tile page uses. A first Molecule can be seeded two ways with
 * identical semantics: the SDK (`client.types.Molecule.from_sdf(...)`) or the
 * picker's own SDF upload control (POST /api/molecules → seed_molecule_from_sdf,
 * dispatch 039). The picker then references the catalogued artifact by hash.
 *
 * Flow PARAMETERS are set through the same `ManifestForm` the Tile page uses
 * (#831 / request 064). Until then this page only *previewed* them, so
 * `full_affdo_sequential` rendered a full description of `reference_level`
 * beside no control to set it and every run silently took the declared
 * defaults. The preview stays — it carries the long descriptions — but it is
 * no longer the only place a parameter appears.
 */
export function SubmitFlowPage(): JSX.Element {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);
  const flows = useFlows();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFlow = searchParams.get("flow");
  const manifest = useFlowManifest(selectedFlow ?? "");
  const artifacts = useArtifacts();
  const submit = useSubmitFlow();
  const uploadMolecule = useUploadMolecule();
  const [selectedArtifacts, setSelectedArtifacts] = React.useState<Record<string, string>>({});
  // The raw record of what the user touched. It is deliberately NOT the
  // dict the form reads or the request body is built from: both of those
  // are `renderableEdits` below, which reconciles this against whatever
  // manifest is current. Untouched parameters come from the manifest's
  // declared defaults at submit time.
  const [parameterEdits, setParameterEdits] = React.useState<Record<string, unknown>>({});

  // Swapping flows must not carry the previous flow's input selections
  // or parameter edits over — the port and parameter names (and their
  // types) are different.
  React.useEffect(() => {
    setSelectedArtifacts({});
    setParameterEdits({});
  }, [selectedFlow]);

  // Flow-level declared inputs, defensively shaped (same posture as the
  // Tile page, Rupert PR-300 r0): an entry without a string `type` is
  // dropped rather than rendered as an untyped picker. Every declared flow
  // input is required — FlowSchema.FlowInput has no `required` flag, and
  // the compiler needs a value for each to resolve its bindings.
  const flowInputs = React.useMemo(() => {
    const raw = (manifest.data?.manifest as { inputs?: unknown } | undefined)?.inputs;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
    const out: Record<string, InputPort> = {};
    for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
      if (!value || typeof value !== "object") continue;
      const v = value as { type?: unknown; description?: unknown };
      if (typeof v.type !== "string") continue;
      out[name] = {
        type: v.type,
        cardinality: "one",
        required: true,
        description: typeof v.description === "string" ? v.description : undefined,
      };
    }
    return out;
  }, [manifest.data]);

  // The user's edits reconciled against the CURRENT manifest — the one
  // dict both the form and the request body are built from, which is what
  // makes "what you see" and "what you submit" the same object rather
  // than two derivations that can disagree.
  //
  // `parameterEdits` is cleared on flow swap but survives a same-flow
  // manifest refetch, so a template redeployed under the open page can
  // leave an edit for a parameter it no longer exposes (Rupert PR-841 r1)
  // or has re-typed under (r2) — and an emptied numeric field leaves a
  // `null` the control does not display either. `renderableParameterEdits`
  // drops exactly those; the form then falls back to the declared default
  // for them, and so does the submission. Feeding this back through
  // ManifestForm's `values` also prunes the dropped entries from state on
  // the user's next edit, so nothing lingers invisibly.
  const renderableEdits = React.useMemo(
    () => renderableParameterEdits(manifest.data?.manifest, parameterEdits),
    [manifest.data, parameterEdits]
  );

  // Every settable parameter at its declared default, overlaid with the
  // reconciled edits. Submitting THIS — rather than only the edits — makes
  // the request say exactly what the user was looking at when they pressed
  // Submit, instead of relying on the server re-deriving defaults from a
  // template that may have been redeployed since the manifest was fetched.
  const submittedParameters = React.useMemo(
    () => ({ ...declaredParameterDefaults(manifest.data?.manifest), ...renderableEdits }),
    [manifest.data, renderableEdits]
  );

  const missingRequiredInputs = React.useMemo(() => {
    if (!flowInputs) return [];
    return Object.keys(flowInputs).filter((name) => !selectedArtifacts[name]);
  }, [flowInputs, selectedArtifacts]);
  // Prefer the overlay endpoint so live run state colors the canvas;
  // fall back to the structural endpoint only when overlay errors
  // (e.g. no database wired). Gating ``useFlowDag`` on the overlay
  // error avoids firing two endpoint hits — each of which re-walks
  // the flows directory — on the happy path. The DAGCanvas accepts
  // either shape.
  const overlay = useFlowDagOverlay(selectedFlow ?? "");
  const structural = useFlowDag(selectedFlow ?? "", { enabled: overlay.isError });
  const dag = overlay.isError ? structural : overlay;

  if (project.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <EmptyState
        title={isNotFound ? "Project not found." : "Could not load project."}
        description={
          isNotFound
            ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
            : project.error instanceof Error
              ? project.error.message
              : "Unknown error."
        }
      />
    );
  }
  if (!project.data) return <></>;

  const onSelectFlow = (name: string): void => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("flow", name);
      return params;
    });
  };

  const onSelectArtifact = (portName: string, contentHash: string | undefined): void => {
    setSelectedArtifacts((prev) => {
      if (contentHash === undefined) {
        const { [portName]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [portName]: contentHash };
    });
  };

  const onSubmit = (): void => {
    if (!selectedFlow) return;
    submit.mutate(
      {
        flow_name: selectedFlow,
        input_artifacts: selectedArtifacts,
        parameters: submittedParameters,
      },
      {
        onSuccess: (data) => {
          toast.success(
            `Flow ${data.flow_name} submitted — dispatching ${data.node_count} node(s). ` +
              `Children appear below and in Runs as they start.`
          );
        },
        onError: (err) => {
          toast.error(`Flow submission failed: ${(err as Error).message}`);
        },
      }
    );
  };

  // The manifest is what tells us which inputs a flow declares, so a flow
  // whose manifest didn't load can't be submitted safely — the backend
  // would 400 on the missing inputs anyway.
  const manifestUnavailable = Boolean(selectedFlow) && !flowInputs;
  const canSubmit =
    Boolean(selectedFlow) &&
    !manifestUnavailable &&
    missingRequiredInputs.length === 0 &&
    !submit.isPending;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/projects" className="hover:text-foreground">
          Projects
        </Link>
        <span aria-hidden>·</span>
        <Link to={`/projects/${project.data.slug}`} className="hover:text-foreground">
          {project.data.slug}
        </Link>
        <span aria-hidden>·</span>
        <Link to={`/projects/${project.data.slug}/submit`} className="hover:text-foreground">
          submit
        </Link>
        <span aria-hidden>·</span>
        <span>flow</span>
      </div>

      <PageHeader
        title="Submit a Flow"
        description="Pick a Flow template, wire its inputs from the catalog, and submit. Each node — and each fan-out child — runs as its own tracked run; the DAG below colors as they land."
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_2fr]">
        <section className="flex flex-col gap-3" aria-labelledby="flow-picker-heading">
          <h2 id="flow-picker-heading" className="label-micro text-muted-foreground">
            Templates
          </h2>
          <FlowPicker
            flows={flows.data}
            selected={selectedFlow}
            onSelect={onSelectFlow}
            isLoading={flows.isLoading}
            error={flows.isError ? (flows.error as Error) : null}
          />
        </section>

        <section className="flex flex-col gap-4" aria-labelledby="flow-preview-heading">
          <div className="flex items-center justify-between gap-2">
            <h2 id="flow-preview-heading" className="label-micro text-muted-foreground">
              Preview
            </h2>
            {selectedFlow && (
              <span className="font-mono text-xs text-muted-foreground">{selectedFlow}</span>
            )}
          </div>
          <Card className="p-card">
            <FlowManifestPreview
              manifest={manifest.data?.manifest}
              isLoading={manifest.isLoading && Boolean(selectedFlow)}
            />
          </Card>

          {selectedFlow && (
            <Card className="p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="label-micro text-muted-foreground">DAG</h3>
                <div className="flex items-center gap-3">
                  {/* Bulk retry surfaces only when the overlay supplies
                   * failed nodes; otherwise it returns null and stays
                   * out of the layout. */}
                  <BulkRetryControl nodes={dag.data?.nodes as DagNodeWithState[] | undefined} />
                  <span className="label-micro text-muted-foreground">
                    {dag.data?.nodes.length ?? 0} nodes · {dag.data?.edges.length ?? 0} edges
                  </span>
                </div>
              </div>
              {dag.isLoading ? (
                <Skeleton className="h-[360px] w-full" />
              ) : dag.isError ? (
                <EmptyState
                  title="Could not load DAG."
                  description={dag.error instanceof Error ? dag.error.message : "Unknown error."}
                />
              ) : dag.data ? (
                <React.Suspense fallback={<Skeleton className="h-[360px] w-full" />}>
                  <DAGCanvas dag={dag.data} />
                </React.Suspense>
              ) : null}
            </Card>
          )}
          {selectedFlow && (
            <Card className="p-card">
              <h3 className="label-micro mb-3 text-muted-foreground">Inputs</h3>
              {manifestUnavailable ? (
                <EmptyState
                  title="Could not read this flow's inputs."
                  description={
                    manifest.isLoading
                      ? "Loading the flow manifest…"
                      : "The flow manifest failed to load, so its declared inputs are unknown."
                  }
                />
              ) : (
                <ArtifactInputPicker
                  inputs={flowInputs}
                  artifacts={artifacts.data ?? []}
                  selected={selectedArtifacts}
                  onChange={onSelectArtifact}
                  onUploadMolecule={(file, declaredCharge) =>
                    uploadMolecule.mutateAsync({ file, declaredCharge })
                  }
                  isLoading={artifacts.isLoading}
                />
              )}
              <SelectedInputPreview selected={selectedArtifacts} />
            </Card>
          )}
          {selectedFlow && (
            <Card className="p-card">
              <h3 className="label-micro mb-3 text-muted-foreground">Parameters</h3>
              {/* A manifest that failed to load would otherwise reach
               * ManifestForm as `undefined` and render "Pick a Flow first." at
               * someone who has already picked one. Same posture as Inputs. */}
              {manifestUnavailable && !manifest.isLoading ? (
                <EmptyState
                  title="Could not read this flow's parameters."
                  description="The flow manifest failed to load, so its declared parameters are unknown."
                />
              ) : (
                <ManifestForm
                  subject="Flow"
                  manifest={manifest.data?.manifest}
                  values={renderableEdits}
                  onChange={setParameterEdits}
                  isLoading={manifest.isLoading}
                />
              )}
            </Card>
          )}
          <div className="flex items-center justify-end gap-3">
            {selectedFlow && missingRequiredInputs.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Select an artifact for: {missingRequiredInputs.join(", ")}
              </p>
            )}
            <Link
              to={`/projects/${project.data.slug}`}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Link>
            <Button
              onClick={onSubmit}
              disabled={!canSubmit}
              aria-disabled={!canSubmit}
              title={
                selectedFlow
                  ? missingRequiredInputs.length > 0
                    ? `Missing required input(s): ${missingRequiredInputs.join(", ")}`
                    : "Submit this Flow"
                  : "Pick a Flow template first"
              }
            >
              <Send className="mr-2 h-4 w-4" aria-hidden />
              {submit.isPending ? "Submitting…" : "Submit Flow"}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
